import React from "react";
import uniq from "lodash/uniq";
import { User } from "~/common/Store/models/User";
import { QuantityDiscount } from "../ProductDetailPageResult";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import shouldDisplayBelowMapPrice from "~/common/Product/shouldDisplayBelowMapPrice";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import getMinBuyForQuantityDropdown from "./AddToCart/getMinBuyForQuantityDropdown";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";

interface Props {
  product: PricingProduct;
  user: User;
  mostExpensiveQuantityDiscount?: QuantityDiscount;
  isGoogleView?: boolean;
  quantityDiscounts: QuantityDiscount[];
  hasLotDiscounts?: boolean;
  showCrossoutPrice?: boolean;
}

const PriceLabelV2 = ({
  product,
  user,
  mostExpensiveQuantityDiscount,
  isGoogleView,
  quantityDiscounts,
  hasLotDiscounts = false,
  showCrossoutPrice = false,
}: Props) => {
  const {
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    minimumBuy,
    mustBuy,
    price,
    unitOfMeasure,
    groupingAttributes,
    startBuy,
  } = product;

  const displayBelowMapPrice = shouldDisplayBelowMapPrice(product, user);

  const displayRequiredCustomization =
    shouldDisplayRequiredCustomization(product);

  const mapStyleG =
    price.minimumAdvertisedPriceProperties?.type?.trim() === "G";
  const displayCustomQuoteText =
    price.displayMinimumAdvertisedPrice &&
    displayBelowMapPrice &&
    user &&
    !mapStyleG;

  const lowestStartQuantityDiscount = [...quantityDiscounts].sort(
    function handleSort(a, b) {
      if (a.startQuantity < b.startQuantity) return -1;
      if (a.startQuantity > b.startQuantity) return 1;
      return 0;
    },
  )[0];

  const lowestEndQuantity = ((): number => {
    const endQuantity = Number(lowestStartQuantityDiscount?.startQuantity) - 1;

    if (mustBuy <= 1) {
      return endQuantity;
    }
    return Math.floor(endQuantity / mustBuy) * mustBuy;
  })();

  const getQuantityDiscountLabel = (): JSX.Element => {
    let text = "Only";

    if (mostExpensiveQuantityDiscount) {
      if (isGoogleView) {
        const updatedMinBuy = getMinBuyForQuantityDropdown({
          minBuy: minimumBuy,
          mustBuy,
        });

        text = `Buy ${updatedMinBuy} or more`;
      } else if (hasLotDiscounts) {
        text = showCrossoutPrice ? "" : "Regularly: ";
      } else {
        text =
          (quantityDiscounts.length === 0 &&
            startBuy > 1 &&
            !showCrossoutPrice) ||
          quantityDiscounts.length > 0
            ? `Buy ${startBuy}${
                typeof lowestEndQuantity === "number" &&
                !isNaN(lowestEndQuantity) &&
                lowestEndQuantity > startBuy
                  ? ` - ${lowestEndQuantity}`
                  : ""
              }`
            : "";
      }
    }

    return (
      <>
        {showCrossoutPrice ? (
          <SaleBadgeAndPricing
            dataTestId="itemSale"
            price={price.price}
            salePricingClassNames="p-0 my-1 text-xs text-gray-800 font-normal"
            saleBadgeExtraClassNames="px-1 mr-1 py-0.5"
          />
        ) : null}
        <label>{text}</label>
      </>
    );
  };

  const getCustomQuoteLabel = () => {
    if (
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
      user.hasWebPlus
    ) {
      return <label>Exclusive Plus Pricing</label>;
    }

    if (
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
      user.platinum?.enabled
    ) {
      return <label>Exclusive Platinum Pricing</label>;
    }

    return (
      <label className="to-see-label">
        {" Custom Quote for "}
        <span className="capitalize">
          {user.companyName ? user.companyName : user.fullName}
        </span>
      </label>
    );
  };

  if (displayRequiredCustomization) {
    if (isGoogleView) {
      return <label>Buy 1 or more</label>;
    }

    return <label>Custom Prices Starting at:</label>;
  }

  if (displayCustomQuoteText) {
    return (
      <>
        {getCustomQuoteLabel()}
        {mostExpensiveQuantityDiscount && getQuantityDiscountLabel()}
      </>
    );
  }

  if (mostExpensiveQuantityDiscount) {
    return getQuantityDiscountLabel();
  }

  let label =
    unitOfMeasure?.toLocaleLowerCase() === "each" ? "Only" : "Each only";

  if (isGoogleView && (minimumBuy > 1 || mustBuy > 1)) {
    label = "From";
  }

  const showFrom =
    groupingAttributes.isGroupMaster &&
    uniq(groupingAttributes.groupMembers.map((item) => item.price.price))
      .length > 1;

  if (showFrom) {
    return <span className="from">From</span>;
  }

  return showCrossoutPrice ? (
    <SaleBadgeAndPricing
      price={price.price}
      displayPriceLabel={true}
      priceLabel={formatAsCurrency.formatIfUndefined(price.price)}
      dataTestId="was-price"
      salePricingClassNames="sale-price"
      saleBadgeExtraClassNames="mr-1 mt-0.5 px-3/4 py-0.5 text-sm"
    />
  ) : (
    <label>{label}</label>
  );
};

export default PriceLabelV2;
